import React, { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
	CarIcon,
	TractorIcon,
	TruckTrailerIcon,
	SuitcaseIcon,
	StorefrontIcon,
} from "@phosphor-icons/react";
import {
	LOAN_TYPE_BUSINESS_WORKING_CAPITAL,
	LOAN_TYPE_BUSINESS_VEHICLE,
	LOAN_TYPE_BUSINESS_EQUIPMENT,
	LOAN_TYPE_BUSINESS_TRUCK,
	stateOption,
	LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY,
	emailTypos,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/forms/context";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import { secureInput } from "@/lib/security";

interface Step1Props {
	loanType?: string;
}

export const PersonalDetails: React.FC<Step1Props> = ({ loanType }) => {
	// Get values and setters from Zustand store
	const { formValues, setFormValue } = useFormDataStore();
	const {
		selectedIndex,
		loanAmountMin,
		setFormError,
		setValidateFormDetails,
		setEnabledFields,
		enabledLoans,
		formContainer,
		toggleTC,
		setToggleTC,
	} = useFormStateStore();

	// Toggle Switch for Fixed Rate
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	const {
		firstName,
		lastName,
		email,
		state,
		loanAmount,
		loanType: zLoanType,
		loanPurpose,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		email: false,
		loanType: false,
		state: false,
		loanAmount: false,
		tc: false,
	});

	const [emailMessage, setEmailMessage] = useState("");
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = 600;
		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes with validation
	const handleFirstNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("firstName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleLastNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("lastName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.email(value);
			setFormValue("email", sanitizedValue.sanitized);

			const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
			setEmailMessage(emailErrorMsg);
		},
		[setFormValue],
	);

	const handleBlur = (field: "firstName" | "lastName" | "email", value: string) => {
		if (!value) return;

		if (field === "email") {
			setFormValue("email", value.toLowerCase());
		} else {
			// Capitalise first character, and convert ALL-CAPS strings to Capitalised
			const forcedCasing =
				value === value.toUpperCase()
					? value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
					: value.charAt(0).toUpperCase() + value.slice(1);

			setFormValue(field, forcedCasing);
		}
	};

	const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		setFormValue("state", value);
		setErrors((prev) => ({ ...prev, state: !value }));
	};

	const handleLoanAmountChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("loanAmount", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				loanAmount: formattedValue < loanAmountMin,
			}));
		},
		[setFormValue, loanAmountMin],
	);

	const handleLoanTypeChange = useCallback(
		(value: string) => {
			setFormValue("loanType", value);
			setErrors((prev) => ({ ...prev, loanType: !value }));
			if (value) {
				setEnabledFields(value);
			}
		},
		[setFormValue, setEnabledFields],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			state: !state,
			loanAmount: !loanAmount || parseFloat(loanAmount.replace(/,/g, "")) < loanAmountMin,
			loanType: !loanType,
			tc: !enabledTC,
		};

		setErrors(newErrors);
		setEmailMessage(emailErrorMsg);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, state, loanAmount, zLoanType, loanType, enabledTC]);

	return (
		<div className="bizF-space-y-2">
			<div
				className={clsx(
					isNavDivSmall ? "bizF-flex-col bizF-gap-x-2" : "bizF-flex-row",
					"bizF-flex md:bizF-flex-row sm:bizF-gap-x-3",
				)}
			>
				<div className="bizF-flex-1">
					<div className="bizF-relative bizF-mt-2">
						<input
							value={firstName}
							onChange={handleFirstNameChange}
							onBlur={(e) => handleBlur("firstName", e.target.value)}
							aria-label="First Name"
							type="text"
							id="firstName"
							placeholder="First Name"
							maxLength={30}
							autoComplete="given-name"
							className={clsx(
								"bizF-capitalize",
								errors.firstName
									? "bizF-ring-red-500 focus:bizF-ring-red-500"
									: "bizF-ring-gray-300 focus:bizF-ring-[--bizF-theme-color]",
								"bizF-block bizF-w-full bizF-rounded-md bizF-shadow-sm bizF-border-0 bizF-py-2.5 bizF-text-gray-900 bizF-ring-1 bizF-outline-1 bizF-outline-gray-900 bizF-ring-inset focus:bizF-ring-1 focus:bizF-ring-inset focus-visible:bizF-outline-[--bizF-theme-color] bizF-pl-3 placeholder:bizF-text-gray-400 sm:bizF-text-md sm:bizF-leading-6",
							)}
						/>
					</div>
					{errors.firstName && (
						<span className="bizF-text-xs bizF-text-red-500">First Name Required</span>
					)}
				</div>
				<div className="bizF-flex-1">
					<div className="bizF-relative bizF-mt-2">
						<input
							value={lastName}
							onChange={handleLastNameChange}
							onBlur={(e) => handleBlur("lastName", e.target.value)}
							aria-label="Last Name"
							type="text"
							id="lastName"
							placeholder="Last Name"
							maxLength={30}
							autoComplete="family-name"
							className={clsx(
								"bizF-capitalize",
								errors.lastName
									? "bizF-ring-red-500 focus:bizF-ring-red-500"
									: "bizF-ring-gray-300 focus:bizF-ring-[--bizF-theme-color]",
								"bizF-block bizF-w-full bizF-rounded-md bizF-shadow-sm bizF-border-0 bizF-py-2.5 bizF-text-gray-900 bizF-ring-1 bizF-outline-1 bizF-outline-gray-900 bizF-ring-inset focus:bizF-ring-1 focus:bizF-ring-inset focus-visible:bizF-outline-[--bizF-theme-color] bizF-pl-3 placeholder:bizF-text-gray-400 sm:bizF-text-md sm:bizF-leading-6",
							)}
						/>
					</div>
					{errors.lastName && (
						<span className="bizF-text-xs bizF-text-red-500">Last Name Required</span>
					)}
				</div>
			</div>

			<div className="bizF-relative bizF-mt-1">
				<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-left-0 bizF-flex bizF-items-center bizF-pl-3">
					<EnvelopeIcon
						className="bizF-h-5 bizF-w-5 bizF-text-gray-400"
						aria-hidden="true"
					/>
				</div>
				<input
					value={email}
					onChange={handleEmailChange}
					onBlur={(e) => handleBlur("email", e.target.value)}
					aria-label="Email Address"
					type="email"
					id="email"
					placeholder="Email Address"
					maxLength={50}
					autoComplete="email"
					className={clsx(
						errors.email
							? "bizF-ring-red-500 focus:bizF-ring-red-500"
							: "bizF-ring-gray-300 focus:bizF-ring-[--bizF-theme-color]",
						"bizF-block bizF-w-full bizF-rounded-md bizF-shadow-sm bizF-border-0 bizF-py-2.5 bizF-pl-10 bizF-text-gray-900 bizF-ring-1 bizF-outline-1 bizF-outline-gray-900 bizF-ring-inset focus:bizF-ring-1 focus:bizF-ring-inset focus-visible:bizF-outline-[--bizF-theme-color] placeholder:bizF-text-gray-400 sm:bizF-text-md sm:bizF-leading-6 bizF-bg-none",
					)}
				/>
			</div>
			{errors.email && emailMessage && (
				<span className="bizF-text-xs bizF-text-red-500">{emailMessage}</span>
			)}

			{enabledLoans.length > 1 && (
				<RadioGroupField
					id="loanType"
					label="Choose a loan type"
					value={loanType || ""}
					options={enabledLoans.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_TYPE_BUSINESS_VEHICLE ? (
								<CarIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_BUSINESS_EQUIPMENT ? (
								<TractorIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_BUSINESS_TRUCK ? (
								<TruckTrailerIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_BUSINESS_WORKING_CAPITAL ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY ? (
								<StorefrontIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						handleLoanTypeChange(value);
					}}
					required={true}
					error={errors.loanType ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={3}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			<div className="bizF-flex bizF-flex-col md:bizF-flex-row bizF-gap-2 bizF-mt-3">
				<div className="bizF-flex-1">
					<div className="bizF-relative">
						<select
							value={state}
							onChange={handleStateChange}
							id="state"
							aria-label="State"
							className={clsx(
								errors.state
									? "bizF-ring-red-500 focus:bizF-ring-red-500"
									: "bizF-ring-gray-300 focus:bizF-ring-[--bizF-theme-color]",
								"bizF-appearance-none bizF-bg-white bizF-block bizF-w-full bizF-rounded-md bizF-border-0 bizF-py-2.5 bizF-pl-3 bizF-pr-10 bizF-text-gray-900 bizF-ring-1 bizF-ring-inset bizF-ring-gray-300 focus:bizF-ring-1 focus-visible:bizF-outline-[--bizF-theme-color] bizF-outline-1 bizF-outline-gray-900 focus:bizF-ring-inset sm:bizF-text-md sm:bizF-leading-6",
							)}
						>
							<option value="" disabled>
								Please choose a state...
							</option>
							{stateOption.map((stateOptions) => (
								<option key={stateOptions.value} value={stateOptions.value}>
									{stateOptions.option}
								</option>
							))}
						</select>
						{errors.state && (
							<span className="bizF-text-xs bizF-text-red-500">
								State is required
							</span>
						)}
					</div>
				</div>
				<div className="bizF-flex-1">
					<div className="bizF-relative">
						<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-left-0 bizF-flex bizF-items-center bizF-pl-3">
							<span className="bizF-text-gray-500 sm:bizF-text-md">$</span>
						</div>
						<input
							value={loanAmount}
							onChange={handleLoanAmountChange}
							aria-label="Loan Amount"
							placeholder={
								loanAmountMin >= 10000
									? `Loan Amount (Min 10K)`
									: loanAmountMin == 5000
										? `Loan Amount (Min 5K)`
										: loanAmountMin == 2000
											? `Loan Amount (Min 2K)`
											: ``
							}
							className={clsx(
								errors.loanAmount
									? "bizF-ring-red-500 focus:bizF-ring-red-500"
									: "bizF-ring-gray-300 focus:bizF-ring-[--bizF-theme-color]",
								"bizF-block bizF-w-full bizF-rounded-md bizF-shadow-sm bizF-border-0 bizF-py-2.5 bizF-pl-7 bizF-pr-12 bizF-text-gray-900 bizF-ring-1 bizF-ring-inset bizF-ring-gray-300 focus:bizF-ring-1 bizF-outline-1 focus:bizF-ring-inset focus-visible:bizF-outline-[--bizF-theme-color] placeholder:bizF-text-gray-400 sm:bizF-text-md sm:bizF-leading-6",
							)}
							type="text"
							id="loan-amount"
							aria-describedby="loan-amount"
							inputMode="numeric"
						/>
						<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-right-0 bizF-flex bizF-items-center bizF-pr-3">
							<span className="bizF-text-gray-500 sm:bizF-text-md">AUD</span>
						</div>
					</div>
					{errors.loanAmount && (
						<span className="bizF-text-xs bizF-text-red-500">
							Minimum loan amount is {loanAmountMin >= 10000 ? `$10,000` : `$5,000`}
						</span>
					)}
				</div>
			</div>
			<div id="tc-toggle" className={clsx("!bizF-mt-4")} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTC} setEnabled={setEnabledTC} />
				{errors.tc && (
					<span className="bizF-block bizF-mt-1 bizF-text-xs bizF-text-red-500">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>
		</div>
	);
};
